<template>
    <div class="h-screen flex overflow-hidden bg-gray-100">
        <!-- Off-canvas menu for mobile -->
        <div class="md:hidden">
            <div @click="sidebarOpen = false"
                 class="fixed inset-0 z-30 bg-gray-600 opacity-0 pointer-events-none transition-opacity ease-linear duration-300"
                 :class="{'opacity-75 pointer-events-auto': sidebarOpen, 'opacity-0 pointer-events-none': !sidebarOpen}"></div>
            <div
                class="fixed inset-y-0 left-0 flex flex-col z-40 max-w-xs w-full bg-gray-100 transform ease-in-out duration-300"
                :class="{'translate-x-0': sidebarOpen, '-translate-x-full': !sidebarOpen}">
                <div class="absolute top-0 right-0 -mr-14 p-1">
                    <button v-if="sidebarOpen" @click="sidebarOpen = false"
                            class="flex items-center justify-center h-12 w-12 rounded-full focus:outline-none focus:bg-gray-600">
                        <svg class="h-6 w-6 text-white" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                </div>
                <div class="flex items-center h-16 flex-shrink-0 px-4 bg-office-primary">
                    <img class="h-auto w-auto" src="/files/logo-2021-white.png" alt="Logo"/>
                </div>
                <div class="flex-1 h-0 overflow-y-auto">
                    <nav class="px-2 py-4">
                        <a
                            v-for="link in links"
                            :href="link.link"
                            :target="link.target ? link.target : false"
                            v-if="link.visible"
                            class="mt-1 group flex items-center px-2 py-2 leading-6 font-medium rounded-md text-base hover:bg-gray-50 focus:outline-none focus:bg-gray-100 transition ease-in-out duration-150"
                            :class="{
                                'bg-gray-200 text-gray-500': link.id === active,
                                'text-gray-500' : link.id !== active,
                                'ml-10': link.sub
                            }"
                        >
                            <i class="fa fa-fw mr-2" :class="[link.icon]"></i>
                            {{ link.name }}
                        </a>
                    </nav>
                </div>
            </div>
        </div>
        <!-- Static sidebar for desktop -->
        <div class="hidden md:flex md:flex-shrink-0">
            <div class="flex flex-col w-64">
                <div class="flex items-center h-16 flex-shrink-0 px-4 bg-office-primary">
                    <img class="h-auto w-auto" src="/files/logo-2021-white.png" alt="Logo"/>
                </div>
                <div class="h-0 flex-1 flex flex-col overflow-y-auto">
                    <!-- Sidebar component, swap this element with another sidebar if you like -->
                    <nav class="flex-1 px-2 py-4 bg-gray-100">
                        <a
                            v-for="link in links"
                            :href="link.link"
                            :target="link.target ? link.target : false"
                            v-if="link.visible"
                            class="mt-1 group flex items-center px-2 py-2 leading-6 font-medium rounded-l-md text-base hover:bg-gray-50 focus:outline-none focus:bg-white transition ease-in-out duration-150"
                            :class="{
                                'bg-gray-200 text-gray-500': link.id === active,
                                'text-gray-500' : link.id !== active,
                                'ml-10': link.sub}"
                        >
                            <i class="fa fa-fw mr-2" :class="[link.icon]"></i>
                            {{ link.name }}
                        </a>
                    </nav>
                </div>
            </div>
        </div>
        <div class="flex flex-col w-0 flex-1 overflow-hidden">
            <div class="relative z-10 flex-shrink-0 flex h-16 bg-office-primary shadow">
                <button @click.stop="sidebarOpen = true"
                        class="px-4 text-white focus:outline-none focus:bg-office-secondary focus:text-white md:hidden">
                    <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M4 6h16M4 12h16M4 18h7"/>
                    </svg>
                </button>
                <div class="flex-1 px-4 flex justify-between">
                    <div class="w-full flex md:ml-0 items-center justify-between">
                        <super-admin-switcher :is-impersonated="isImpersonated" :csrf="csrf"></super-admin-switcher>
                        <language-select class="mb-8"></language-select>
                    </div>

                    <div class="ml-4 flex items-center md:ml-6 flex-shrink-0">
                        <form action="/admin/logout" method="post" v-if="user">
                            <input type="hidden" name="_token" :value="csrf">
                                <button-secondary type="submit">
                                  Logout
                                </button-secondary>
                        </form>
                    </div>
                </div>
            </div>
            <main class="flex-1 relative z-0 overflow-y-auto sm:py-6 focus:outline-none" tabindex="0">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                    <!-- Replace with your content -->
                    <slot name="left"></slot>
                    <slot></slot>
                    <!-- /End replace -->
                </div>
            </main>
        </div>

        <error-response :error="error" @delete="error=null"></error-response>

        <alert ref="alert"></alert>

        <email ref="email"></email>

        <portal-target name="modal"></portal-target>
        <portal-target name="dialog"></portal-target>
        <portal-target name="error"></portal-target>

    </div>
</template>

<script>
import ErrorResponse from "./ErrorResponse";
import Alert from "./UI/Alert";
import Email from "./Email.vue";
import ButtonSecondary from "./UI/ButtonSecondary.vue"
import SuperAdminSwitcher from "./Super/SuperAdminSwitcher.vue";
import LanguageSelect from "./Settings/LanguageSelect.vue";

export default {
    name: "App",
    components: {LanguageSelect, SuperAdminSwitcher, Alert, ErrorResponse, ButtonSecondary, Email},
    props: ['active', 'group', 'user', 'isImpersonated', 'csrf', 'can'],

    provide() {
        return {
            admin: this.user,
            group: this.group,
            can: this.can,
            user: this.user,
        }
    },

    mounted() {
        this.$root.$on('error', (e) => {
            this.error = e;
        });

        this.$root.$on('alert', (msg) => {
            this.$refs.alert.open(msg);
        });

        this.$root.$on('email', (msg) => {
            this.$refs.email.open(msg);
        });

        this.$root.$i18n.locale = this.user.language;
    },

    data: () => ({
        sidebarOpen: false,
        open: false,
        error: null,
    }),

    computed: {
        links() {
            if (!this.can) {
                return [];
            }
            
            if (!this.user) {
                return [];
            }

            let items = [
                {
                    link: '/admin/pages/dashboard',
                    name: 'Dashboard',
                    icon: 'fa-home',
                    id: 'dashboard',
                    visible: true,
                },
                {
                    link: '/admin/pages/users',
                    name: 'Benutzer',
                    icon: 'fa-users',
                    id: 'users',
                    visible: this.can.viewAny.user,
                },
                {
                    link: '/admin/notifications',
                    name: 'Benachrichtigungen',
                    icon: 'fa-bullhorn',
                    id: 'notifications',
                    visible: this.can.viewAny.user && this.can.viewAny.notification,
                    sub: true,
                },
                {
                    link: '/admin/permissions',
                    name: 'Berechtigungen',
                    icon: 'fa-lock',
                    id: 'permissions',
                    visible: this.can.viewAny.user && this.can.viewAny.permission,
                    sub: true,
                },

                {
                    link: '/admin/pages/user_invitees',
                    name: 'Einladungen',
                    icon: 'fa-paper-plane',
                    id: 'user_invitees',
                    visible: this.can.viewAny.user && this.can.viewAny.user_setup,
                    sub: true,
                },
                {
                    link: '/admin/pages/user_attendance',
                    name: 'Anwesenheit',
                    icon: 'fa-map-marker',
                    id: 'user_attendance',
                    visible: this.can.viewAny.user && this.can.viewAny.user_attendance,
                    sub: true,
                },
                {
                    link: '/admin/pages/user_access',
                    name: 'Nutzerzugriff',
                    icon: 'fa-map-marker',
                    id: 'user_access',
                    visible: false,
                    sub: true,
                },
            ];

            if (this.group.allocation_mode !== 'fcfs') {
                items = items.concat({
                        link: '/admin/pages/desks',
                        name: 'Schreibtische',
                        icon: 'fa-desktop',
                        id: 'desks',
                        visible: this.can.viewAny.desk,
                    },
                    {
                        link: '/admin/pages/desk-attributes',
                        name: 'Austattung ',
                        icon: 'fa-gear',
                        id: 'desk-attributes',
                        visible: this.can.viewAny.desk && this.can.create.desk_attribute,
                        sub: true,
                    },
                    {
                        link: '/admin/pages/allocations',
                        name: 'Buchungsregeln',
                        icon: 'fa-random',
                        id: 'allocations',
                        visible: this.can.viewAny.desk && this.can.viewAny.allocation && (this.group.allocation_mode === 'group' || this.group.allocation_mode === 'group_fcfs'),
                        sub: true,
                    },
                    {
                        link: '/admin/pages/matrix',
                        name: 'Buchungsregeln',
                        icon: 'fa-random',
                        id: 'matrix',
                        visible: this.can.viewAny.desk && this.can.viewAny.allocation && this.group.allocation_mode === 'matrix',
                        sub: true,
                    },
                    {
                        link: '/admin/pages/schedules',
                        name: 'Buchungen',
                        icon: 'fa-clock-o',
                        id: 'schedules',
                        visible: this.can.viewAny.desk && this.can.viewAny.schedule,// this.group.allocation_mode === 'algorithm',
                        sub: true,
                    },
                    {
                        link: '/admin/pages/error_messages',
                        name: 'Meldungen',
                        icon: 'fa-bar-chart',
                        id: 'error-messages',
                        visible: this.can.viewAny.desk,// this.group.allocation_mode === 'algorithm',
                        sub: true,
                    },
                    {
                        link: '/admin/pages/attributes',
                        name: 'Ausstattung',
                        icon: 'fa-archive',
                        id: 'attributes',
                        visible: this.can.viewAny.desk && this.can.viewAny.desk_attribute,
                        sub: true,
                    },
                    {
                        link: '/admin/pages/conference_rooms',
                        name: 'Konferenzräume',
                        icon: 'fa-users',
                        id: 'conference_rooms',
                        visible: this.can.viewAny.conference_room,
                    },
                    {
                        link: '/admin/pages/conference_room_schedules',
                        name: 'Buchungen',
                        icon: 'fa-clock-o',
                        id: 'EquipmentCluster',
                        visible: this.can.viewAny.conference_room && this.can.viewAny.conference_room_booking,
                        sub: true,
                    },
                    {
                        link: '/admin/pages/equipment',
                        name: 'Ausstattung',
                        icon: 'fa-archive',
                        id: 'EquipmentCluster',
                        visible: this.can.viewAny.conference_room && this.can.viewAny.equipment,
                        sub: true,
                    },
                    {
                        link: '/admin/pages/conference_rooms_statistic',
                        name: 'Statistik',
                        icon: 'fa-line-chart',
                        id: 'conference_rooms_statistic',
                        visible: this.can.viewAny.conference_room,
                        sub: true,
                    },
                );
            }

            this.group.bookable_categories.forEach(category => {
                items.push({
                    link: '/admin/pages/bookable_categories/' + category.id,
                    name: category.name,
                    icon: category.icon,
                    id: category.id.toString(),
                    visible: this.can.viewAny.bookable_category && this.can.viewAny.bookable_booking,
                },
                {
                    link: '/admin/pages/bookable_schedules/' + category.id,
                    name: 'Buchungen',
                    icon: 'fa-clock-o',
                    id: 'bookable_schedules',
                    visible:  this.can.viewAny.bookable_category && this.can.viewAny.bookable_booking,
                    sub: true,
                },
                {
                    link: '/admin/pages/equipment',
                    name: 'Ausstattung',
                    icon: 'fa-archive',
                    id: 'EquipmentCluster',
                    visible: this.can.viewAny.bookable_category && this.can.viewAny.bookable_booking && this.can.viewAny.equipment,
                    sub: true,
                },
                {
                    link: '/admin/pages/bookable_statistic/' + category.id,
                    name: 'Statistik',
                    icon: 'fa-line-chart',
                    id: 'bookable_statistic',
                    visible: this.can.viewAny.bookable_category && this.can.viewAny.bookable_booking,
                    sub: true,
                },)
            })


            if (this.group.allocation_mode !== 'fcfs') {
                items = items.concat(
                    {
                        link: '/admin/statistics',
                        name: 'Statistik',
                        icon: 'fa-line-chart',
                        id: 'statistics',
                        visible: this.user.is_admin,
                    },

                    {
                    link: '/admin/pages/heatmap',
                    name: 'Heatmap',
                    icon: 'fa-map',
                    id: 'heatmap',
                    visible: this.user.is_admin,
                    sub: true,
                },
                );
            }

            if (this.group.subscription_required) {
                items = items.concat([
                    {
                        link: '/admin/plans',
                        name: 'Tickets',
                        icon: 'fa-ticket',
                        id: 'tickets',
                        visible: true,
                    },
                ]);
            }

            if (this.group.voucher_required) {
                items = items.concat([
                    {
                        link: '/admin/vouchers',
                        name: 'Voucher',
                        icon: 'fa-ticket',
                        id: 'vouchers',
                        visible: true,
                    },
                ]);
            }

            items = items.concat([
                {
                    link: '/admin/group',
                    name: 'Einstellungen',
                    icon: 'fa-cog',
                    id: 'settings',
                    visible: this.can.viewAny.setting,
                },

                {
                    link: '/admin/pages/faq',
                    name: 'FAQ',
                    icon: 'fa-solid fa-question',
                    id: 'faq',
                    visible: true,
                },

                {
                    link: '/weblogin',
                    name: 'App',
                    icon: 'fa-mobile',
                    id: 'weblogin',
                    visible: true,
                    target: '_blank',
                },
            ]);

            return items;
        }
    }
}
</script>

<style scoped>

</style>
